import { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { CheckCircle2, Sparkles } from 'lucide-react';

interface OnboardingCelebrationModalProps {
  open: boolean;
  onClose: () => void;
}

function launchConfetti() {
  const duration = 3500;
  const end = Date.now() + duration;

  const colors = ['#2db1c3', '#58BDB3', '#f9a8d4', '#fbbf24', '#a78bfa', '#34d399'];

  const frame = () => {
    confetti({
      particleCount: 6,
      angle: 60,
      spread: 65,
      origin: { x: 0, y: 0.6 },
      colors,
      zIndex: 9999,
    });
    confetti({
      particleCount: 6,
      angle: 120,
      spread: 65,
      origin: { x: 1, y: 0.6 },
      colors,
      zIndex: 9999,
    });
    if (Date.now() < end) {
      requestAnimationFrame(frame);
    }
  };
  frame();

  // Burst central
  setTimeout(() => {
    confetti({
      particleCount: 120,
      spread: 100,
      origin: { x: 0.5, y: 0.45 },
      colors,
      zIndex: 9999,
    });
  }, 300);
}

export function OnboardingCelebrationModal({ open, onClose }: OnboardingCelebrationModalProps) {
  const firedRef = useRef(false);

  useEffect(() => {
    if (open && !firedRef.current) {
      firedRef.current = true;
      launchConfetti();
    }
    if (!open) {
      firedRef.current = false;
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.55)' }}
    >
      <div
        className="relative w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl"
      >

        {/* Gradient header */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#0f7a8a] via-[#2db1c3] to-[#58BDB3] px-8 pt-10 pb-8 text-center">
          {/* Decorative blobs */}
          <div className="absolute -top-6 -left-6 h-24 w-24 rounded-full bg-white/10" />
          <div className="absolute -bottom-4 -right-4 h-20 w-20 rounded-full bg-white/10" />
          <div className="absolute top-4 right-12 h-10 w-10 rounded-full bg-white/10" />

          {/* Logo + check */}
          <div className="relative mb-4 flex justify-center">
            <div className="relative">
              <img
                src="/ISO_TIPOMESTLY.png"
                alt="Mestly"
                className="h-16 w-16 object-contain drop-shadow-xl"
              />
              <div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-400 shadow-lg">
                <CheckCircle2 className="h-4 w-4 text-white" />
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-black text-white tracking-tight leading-tight">
            ¡Felicitaciones! 🎉
          </h2>
          <p className="mt-1 text-sm font-medium text-white/80">
            Tu negocio ya está listo en Mestly
          </p>
        </div>

        {/* Body */}
        <div className="px-8 py-7">
          <p className="text-center text-slate-600 text-sm leading-relaxed">
            Has completado la configuración inicial. A partir de ahora puedes gestionar{' '}
            <span className="font-semibold text-slate-800">citas, clientes, profesionales y reportes</span>{' '}
            desde un solo lugar.
          </p>

          <div className="mt-5 space-y-2.5">
            {[
              '📅 Agenda y gestiona citas fácilmente',
              '👥 Administra tu base de clientes',
              '📊 Revisa reportes y finanzas',
              '🔗 Activa tu reserva online',
            ].map(item => (
              <div key={item} className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-2.5">
                <span className="text-sm text-slate-700">{item}</span>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="mt-6 w-full rounded-2xl bg-gradient-to-r from-[#0f7a8a] to-[#58BDB3] py-4 text-sm font-black tracking-wide text-white shadow-lg hover:shadow-xl hover:opacity-95 transition-all active:scale-[0.98]"
          >
            <span className="flex items-center justify-center gap-2">
              <Sparkles className="h-4 w-4" />
              MANEJAR MI NEGOCIO
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
